export type InstallResult = "accepted" | "dismissed" | "unavailable" | "failed";

type PromptChoice = { outcome?: string } | undefined;
type DeferredInstallEvent = Event & {
  prompt(): Promise<PromptChoice> | PromptChoice;
  userChoice?: Promise<PromptChoice>;
};

/** One deferred browser prompt for this page; installed status is never persisted. */
export class InstallPrompt {
  private deferred: DeferredInstallEvent | null = null;
  private accepted = false;
  private requesting = false;
  private readonly listeners = new Set<() => void>();
  private readonly standalone: MediaQueryList;
  private readonly fullscreen: MediaQueryList;

  /** Construct before asynchronous boot work so an early browser prompt is not missed. */
  constructor() {
    window.addEventListener("beforeinstallprompt", (event) => {
      event.preventDefault();
      const deferred = event as DeferredInstallEvent;
      this.deferred = !this.installed && typeof deferred.prompt === "function" ? deferred : null;
      this.notify();
    });
    window.addEventListener("appinstalled", () => {
      this.accepted = true;
      this.deferred = null;
      this.notify();
    });
    this.standalone = window.matchMedia("(display-mode: standalone)");
    this.fullscreen = window.matchMedia("(display-mode: fullscreen)");
    const changed = () => this.notify();
    for (const query of [this.standalone, this.fullscreen]) {
      if (query.addEventListener) query.addEventListener("change", changed);
      else query.addListener(changed);
    }
    document.addEventListener("fullscreenchange", changed);
  }

  get installed(): boolean {
    return (
      this.accepted ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true ||
      this.standalone.matches ||
      (this.fullscreen.matches && !document.fullscreenElement)
    );
  }

  get available(): boolean {
    return this.deferred !== null && !this.installed && !this.requesting;
  }

  get busy(): boolean {
    return this.requesting;
  }

  subscribe(callback: () => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  async request(): Promise<InstallResult> {
    if (!this.available) return "unavailable";
    const deferred = this.deferred!;
    this.deferred = null; // A browser event can prompt only once, including on failure.
    this.requesting = true;
    this.notify();
    try {
      // Invoke before the first await, while the calling tap/key still has user activation.
      const prompted = deferred.prompt();
      const choice = deferred.userChoice;
      const [promptResult, userChoice] = await Promise.all([prompted, choice]);
      const outcome = (userChoice ?? promptResult)?.outcome;
      if (outcome === "accepted") {
        this.accepted = true;
        this.deferred = null;
        return "accepted";
      }
      return "dismissed";
    } catch {
      return "failed";
    } finally {
      this.requesting = false;
      this.notify();
    }
  }

  private notify(): void {
    for (const callback of this.listeners) callback();
  }
}
